import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, TrendingUp, TrendingDown, DollarSign, Percent } from "lucide-react";
import { PlanGate } from "@/components/enterprise-gate";
import { useBranch } from "@/context/branch";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

function fmt(v: number) {
  return `₦${v.toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;
}

function Row({ label, value, sub, positive, muted, indent }: { label: string; value: string; sub?: string; positive?: boolean; muted?: boolean; indent?: boolean }) {
  return (
    <div className={`flex items-center justify-between py-3 ${indent ? "pl-6" : ""} ${!muted ? "border-b border-border/50" : ""}`}>
      <span className={`text-sm ${muted ? "text-muted-foreground" : "font-medium"}`}>{label}</span>
      <div className="text-right">
        <span className={`font-semibold ${positive === true ? "text-emerald-600" : positive === false ? "text-destructive" : ""}`}>{value}</span>
        {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
      </div>
    </div>
  );
}

function PLContent() {
  const [period, setPeriod] = useState("month");
  const { activeBranchId } = useBranch();

  const { data, isLoading } = useQuery({
    queryKey: ["accounting-pl", period, activeBranchId],
    queryFn: async () => {
      const params = new URLSearchParams({ period });
      if (activeBranchId) params.set("branchId", String(activeBranchId));
      const res = await fetch(`${BASE}/api/accounting/profit-loss?${params}`, { credentials: "include" });
      return res.json();
    },
  });

  if (isLoading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <span className="text-sm text-muted-foreground">Period:</span>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="week">This Week</SelectItem>
            <SelectItem value="month">This Month</SelectItem>
            <SelectItem value="quarter">This Quarter</SelectItem>
            <SelectItem value="year">This Year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Revenue (excl. net VAT)</span>
              <DollarSign className="w-4 h-4 text-blue-500" />
            </div>
            <div className="text-xl font-bold">{fmt(data?.revenue ?? 0)}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{data?.transactionCount ?? 0} sales</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-orange-400">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Cost of Goods</span>
              <TrendingDown className="w-4 h-4 text-orange-400" />
            </div>
            <div className="text-xl font-bold">{fmt(data?.costOfGoods ?? 0)}</div>
          </CardContent>
        </Card>
        <Card className={`border-l-4 ${(data?.netProfit ?? 0) >= 0 ? "border-l-emerald-500" : "border-l-red-500"}`}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Net Profit</span>
              <TrendingUp className={`w-4 h-4 ${(data?.netProfit ?? 0) >= 0 ? "text-emerald-500" : "text-destructive"}`} />
            </div>
            <div className={`text-xl font-bold ${(data?.netProfit ?? 0) >= 0 ? "text-emerald-600" : "text-destructive"}`}>{fmt(data?.netProfit ?? 0)}</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Net Margin</span>
              <Percent className="w-4 h-4 text-purple-500" />
            </div>
            <div className="text-xl font-bold">{data?.netMargin ?? 0}%</div>
          </CardContent>
        </Card>
      </div>

      {/* P&L Statement */}
      <Card>
        <CardHeader><CardTitle>Income Statement</CardTitle></CardHeader>
        <CardContent className="pt-0">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider pb-2 pt-2">Revenue (VAT excluded)</div>
          <Row label="Sales Revenue, net of VAT" value={fmt(data?.revenue ?? 0)} positive={true} />

          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider pb-2 pt-4">Cost of Goods Sold</div>
          <Row label="Product Cost (COGS)" value={`(${fmt(data?.costOfGoods ?? 0)})`} />

          <div className="py-3 border-b border-border flex justify-between font-bold">
            <span>Gross Profit</span>
            <span className={(data?.grossProfit ?? 0) >= 0 ? "text-emerald-600" : "text-destructive"}>{fmt(data?.grossProfit ?? 0)}</span>
          </div>
          <div className="text-xs text-muted-foreground text-right py-1">Gross margin: {data?.grossMargin ?? 0}%</div>

          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider pb-2 pt-4">Operating Expenses</div>
          <Row label="Total Expenses" value={`(${fmt(data?.expenses ?? 0)})`} />

          <div className="py-3 flex justify-between font-bold text-base border-t-2 border-border mt-2">
            <span>Net Profit / (Loss)</span>
            <span className={(data?.netProfit ?? 0) >= 0 ? "text-emerald-600" : "text-destructive"}>
              {(data?.netProfit ?? 0) < 0 ? `(${fmt(Math.abs(data?.netProfit ?? 0))})` : fmt(data?.netProfit ?? 0)}
            </span>
          </div>
          <div className="text-xs text-muted-foreground text-right">Net margin: {data?.netMargin ?? 0}%</div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function AccountingProfitLoss() {
  const { activeBranch } = useBranch();
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Profit & Loss — {activeBranch?.name ?? "All Branches"}</h1>
        <p className="text-muted-foreground mt-1">Income statement for the selected location.</p>
      </div>
      <PlanGate allowedPlans={["pro", "enterprise"]} feature="Profit & Loss Statement">
        <PLContent />
      </PlanGate>
    </div>
  );
}
